import React from 'react';
import { PROFILE_PAGES } from './constants';
import ErrorFeedbackBar from '../global/ErrorFeedbackBar';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import { statusSelector } from '../../redux/selectors/global';
import { userInfoSelector } from '../../redux/selectors/auth';
import withShipment from '../../withShipment';
import AllLocationPlan from './AllLocationPlan';
import { availableCompaniesSelector } from '../../redux/selectors/account';
import { listAvailableCompanies } from '../../redux/actions/settings';
import LocationDetailView from './LocationDetailView';
import { fetchLocationDetail } from '../../redux/actions/userManagement';
import { refreshUserInfo } from '../../redux/actions/auth';
import { locationDetailSelector } from '../../redux/selectors/userManagement';
import CustomToolbar from '../global/CustomToolbar';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import Paper from '@material-ui/core/Paper';

class UserProfile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: PROFILE_PAGES.LOCATION_PLAN,
      feedbackBarOpen: false,
      error: false,
      feedbackStatus: '',
      feedbackMessage: '',
      selectedLocation: null,
    };
  }

  componentDidMount() {
    this.props.listAvailableCompanies();
  }

  // Detects ?tab=subscription (or any tab=…) on the URL and auto-opens
  // the user's location detail view once the companies list has loaded.
  // Used by the trial-grace banner button and any external link that
  // wants to drop the user straight into their subscription screen
  // without making them click through the location picker.
  componentDidUpdate(prevProps, prevState) {
    // First time availableCompanies populates after mount, check the
    // query string and auto-navigate if the URL asked for a deep-link.
    const justLoaded =
      (!prevProps.availableCompanies ||
        prevProps.availableCompanies.length === 0) &&
      this.props.availableCompanies &&
      this.props.availableCompanies.length > 0;

    if (
      justLoaded &&
      this.state.currentPage === PROFILE_PAGES.LOCATION_PLAN &&
      !this.state.selectedLocation
    ) {
      this.tryDeepLink();
    }

    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar(this.props.status.statusText, 'error');
    }
  }

  // Reads ?tab=… from the current URL and, when a single primary
  // location can be unambiguously chosen, jumps straight to its
  // LocationDetailView. The default tab in that view is already
  // 'subscription', so /profile?tab=subscription lands exactly where
  // the user expects. Multi-location admins still see the picker —
  // there's no obvious right answer for "which location does the
  // subscription tab refer to" when they manage many.
  tryDeepLink = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (!tabParam) return;

      const companies = this.props.availableCompanies || [];
      // Prefer the user's home company; fall back to the only listed
      // company when there's exactly one.
      const myCompanyId =
        this.props.user?.company || this.props.user?.selectedLocationId;
      let target = null;
      if (myCompanyId) {
        target = companies.find((c) => c.companyId === myCompanyId) || null;
      }
      if (!target && companies.length === 1) {
        target = companies[0];
      }
      if (!target) return;

      this.handleOpenLocation(target);
    } catch (e) {
      // best-effort deep-link; on any parse error fall back to the
      // standard picker UX.
    }
  };

  handleBack = () => {
    this.setState({ selectedLocation: null }, () => {
      this.setCurrentPage(PROFILE_PAGES.LOCATION_PLAN, {});
    });
  };

  handleOpenLocation = (location) => {
    this.setState({ selectedLocation: location }, () => {
      this.props.fetchLocationDetail({ locationId: location.companyId });
      this.setCurrentPage(PROFILE_PAGES.LOCATION_DETAIL_VIEW, {});
    });
  };

  renderAllLocation = () => {
    return (
      <AllLocationPlan
        user={this.props.user}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        availableCompanies={this.props.availableCompanies}
        handleOpenLocation={this.handleOpenLocation}
      />
    );
  };

  renderDetailView = () => {
    return (
      <LocationDetailView
        user={this.props.user}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        location={this.state.selectedLocation}
        handleBack={this.handleBack}
        locationDetail={this.props.locationDetail}
        fetchLocationDetail={this.props.fetchLocationDetail}
        refreshUserInfo={this.props.refreshUserInfo}
      />
    );
  };

  setCurrentPage = (newPage) => {
    this.setState({
      currentPage: newPage,
    });
  };

  handleDeleteRow = (deleteId) => {
    this.props.deleteManageBonus({ manageBonusId: deleteId });
  };

  displayCurrentPage = () => {
    switch (this.state.currentPage) {
      case PROFILE_PAGES.LOCATION_PLAN:
        return this.renderAllLocation();
      case PROFILE_PAGES.LOCATION_DETAIL_VIEW:
        return this.renderDetailView();
      default:
        return <div>We encountered an error. Please refresh the page.</div>;
    }
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  render() {
    const isDetail =
      this.state.currentPage === PROFILE_PAGES.LOCATION_DETAIL_VIEW;
    const title = 'My Account';

    const headerButtons = isDetail
      ? [
          {
            icon: <KeyboardBackspaceIcon />,
            handler: this.handleBack,
            title: 'Back',
          },
        ]
      : [];

    return (
      <React.Fragment>
        <Paper className="paper">
          <ErrorFeedbackBar />
          <FeedbackBar
            open={this.state.feedbackBarOpen}
            handleClose={this.closeFeedbackBar}
            severity={this.state.feedbackStatus}
            message={this.state.feedbackMessage}
          />
          <CustomToolbar title={title} headerButtons={headerButtons} />
          {this.displayCurrentPage()}
        </Paper>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  status: statusSelector(state),
  user: userInfoSelector(state),
  availableCompanies: availableCompaniesSelector(state),
  locationDetail: locationDetailSelector(state),
});

const actionCreators = {
  listAvailableCompanies,
  fetchLocationDetail,
  refreshUserInfo,
};

export default withShipment(
  {
    actionCreators,
    mapStateToProps,
  },
  UserProfile
);
